/**
 * Netx.Routers.App is the class of our sole router instance, Netx.router
 * @class
 * @param {object} opts Options
 * @extends Backbone.ES6Router
 * @todo: This is an interesting idea
 * http://stackoverflow.com/questions/25806608/how-to-detect-browser-back-button-event-cross-browser
 */
import Backbone from 'backbone';
import _ from 'underscore';
import {session} from '@netx/core-auth/collections/singleton/session';
import coreConfig from '@netx/core-next/config';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import {escapeURI} from '@netx/core/utils/escapeURI';
import {unescapeURI} from '@netx/core/utils/unescapeURI';

// base64 search routes, good times! ¯\_(ツ)_/¯
// we need to unescape and then re-escape since things get escaped automagically
// by either the browser, backbone, or the two
// note: need to trim and re-add the base route as that will get mangled in an escape
const BASE64_ROUTES_BASES = ['search/attribute/history/', 'search/advanced/'];
const routeIsBase64 = (route) =>
	BASE64_ROUTES_BASES.find((base) => route.includes(base));
const correctBase64RouteIfNeeded = (route) => {
	const base = routeIsBase64(route);
	if (!base) return route;
	return base + escapeURI(unescapeURI(route.replace(base, '')));
};

/**
 * Only the most recent interceptor will be used. The reason for allowing the setting this
 * way is to try and prevent one view creating while another is removing, and the removing one
 * removed its interceptor
 */
const routeInterceptors = new Map();

/*****************
 * NetX extensions to Backbone.History:
 *
 * Certain parts of X7 need to know whether they're being executed in the context
 * of a route change.  Backbone.History.isRouting() returns the boolean value of a flag
 * that is set when Backbone.History.loadUrl() begins, and cleared when it ends.
 *
 */
const History = Backbone.History;
History.prototype.loadUrl = _.wrap(
	History.prototype.loadUrl,
	function (loadUrl) {
		this._isRouting = true;
		var args = _.rest(arguments);
		var ret = loadUrl.apply(this, args);
		delete this._isRouting;
		return ret;
	},
);

History.prototype.isRouting = function () {
	return this._isRouting || false;
};

const Router = Backbone.Router;
class ES6Router {
	/** @override */
	constructor(opts) {
		Router.apply(this, arguments);
	}
}
_.extend(ES6Router.prototype, Router.prototype);
Backbone.ES6Router = ES6Router;

class AppRouter extends ES6Router {
	ready = false;

	/** @override */
	constructor(opts = {}) {
		super(opts);

		/** @type {array} */
		this.history = [];

		/**
		 * @type {number}
		 * @default
		 */
		this.size = opts.size || 50;

		this.listenTo(this, 'route', this.routeChanged);
		// listen for route paging from pager.js
		this.listenTo(dispatcher, 'router:page', (page, routeContext) => {
			let {hash} = window.location;
			// We need to escape things like `'` (%27) and ` ` (%20)
			// Our `routeContext` encodes, but only some things are encoded in the route by backbone, like spaces,
			// where things like apostrophes are not encoded by backbone ¯\_(ツ)_/¯
			if (!unescapeURI(hash).includes(unescapeURI(routeContext))) return;

			if (!routeIsBase64(routeContext)) {
				// We have to handle this a bit specially - see the wonderful search controller
				hash = hash.replace(/ /g, '%20');
			}

			// see notes about function
			routeContext = correctBase64RouteIfNeeded(routeContext);

			let pageRoute = `/page/${page}`;

			// Do not specify a page in the hash if one was not specified already
			// (can end up in a loop if you use the browser's back button)
			if (page === 1 && !/page\/\d/.test(hash)) {
				pageRoute = '';
			}

			this.navigate(`${routeContext}${pageRoute}`, true);
		});

		// USE RESPONSIBLY
		// Currently, "routed" only gets dispatched when {trigger:false} is not set.
		// This code is for skins, etc that have pieces that would like to know when the route changes
		// whether or not it was silenced.
		// A good example:
		// When you run a search and then navigate into an asset detail view- when done you close the detail
		// view and return to your search results... Rather than running the search over again we put the
		// search string back into the hash and silence the change.
		// @fires 'hash:changed' and provides newHash and oldHash (if you are into that sort of thing)

		// In IE originalEvent.newURL and originalEvent.oldURL are not a thing. We will fake it.
		if (_.isUndefined(this.oldHash)) {
			this.oldHash = window.location.hash ? window.location.hash : '';
		}
		$(window).on('hashchange.router', (e) => {
			const newHash = window.location.hash;
			dispatcher.trigger(
				'hash:changed',
				newHash,
				this.oldHash ? this.oldHash : '',
			);
			this.oldHash = newHash;
		});
	}

	/**
	 * Add route interceptor
	 * Note: Only the most recent interceptor will be used. The reason for allowing the setting this
	 * way is to try and prevent one view creating while another is removing, and the removing one
	 * removed its interceptor
	 * @param {string} key
	 * @param {Function} func
	 */
	addRouteInterceptor(key, func) {
		routeInterceptors.set(key, func);
	}

	/**
	 * Remove route interceptor
	 * @param {string} key
	 * @param {Function} func
	 */
	removeRouteInterceptor(key) {
		routeInterceptors.delete(key);
	}

	/**
	 * Rewind the route one step.  Just like window.history.back(), except it also manages our
	 * local history cache.
	 *
	 * WARNING: intersecting back() with push()/pop() can lead to unknown, probably-wrong behavior.
	 * @param {object} opts Options
	 */
	back(opts = {}) {
		if (this.history.length > 1) {
			window.history.back();
		} else {
			this.home(opts);
		}
	}

	/**
	 * @param {string} page Page
	 */
	// TODO: provide a proper 404 error here.
	defaultRoute(page) {
		console.warn('The page you are trying to reach: %s, does not exist.', page);
	}

	/**
	 * @method
	 * @param {string} route Route event (or route if ananymous function was used)
	 */
	routeChanged(route) {
		// TODO: This is a band-aid until we can get some finer
		// control over our routes (white listing etc)
		const validUnauthenticatedRoutes = [
			'auth:user:login',
			'auth:user:register:approvalNeeded',
			'auth:user:register',
			'auth:user:password:reset',
			'auth:user:password:change',
			'auth:user:sessionKey',
			// share request bundle will handle invalid token messaging
			'share:request',
		];
		if (
			session.requiresLogin() &&
			!session.authenticated() &&
			!_.contains(validUnauthenticatedRoutes, route)
		) {
			// If empty route send them to the login page
			if (route === '') {
				dispatcher.trigger('auth:session:invalid');
				return;
			}
			return window.location.reload();
		}

		// Maintains routing history. Keeps up to the value of 'this.size' in history.
		// Accessible from the history object where index 0 is the current route
		if (this.history.length >= this.size) {
			this.history.pop();
		}

		const oldRoute = this.history[0];
		if (Backbone.history.fragment != history[0]) {
			this.history.unshift(Backbone.history.fragment);
		}

		// Triggers a decoupled 'routed' event that view instances can listen to
		// to perform custom tasks.  Ex. Netx.views.assetDetailView listens to the
		// dispatcher for the 'routed' event and pauses the video player.
		dispatcher.trigger('routed', Backbone.history.fragment, oldRoute);
	}

	/**
	 * Support promises
	 * @override
	 * @param {string} route Route
	 * @param {string} name Route name
	 * @param {function} callback Route collback
	 */
	route(route, name, callback) {
		if (!_.isRegExp(route)) {
			route = this._routeToRegExp(route);
		}
		if (_.isFunction(name)) {
			callback = name;
			name = '';
		}
		if (!callback) {
			callback = this[name];
		}

		Backbone.history.route(route, (fragment) => {
			const args = this._extractParameters(route, fragment);

			const _route = () => {
				// Strip trailing lone slash `asset/3/` -> `asset/3`
				if (fragment.length - 1 === fragment.lastIndexOf('/')) {
					fragment = fragment.substr(0, fragment.length - 1);
					window.location.hash = `#${fragment}`;
				}

				const ret = this.execute(callback, args, name);

				// Execute route
				const executeRoute = () => {
					this.trigger.apply(this, [`route:${name}`].concat(args));
					this.trigger('route', name, args);
					Backbone.history.trigger('route', this, name, args);
				};

				if (!_.isPromise(ret)) {
					ret !== false && executeRoute();
					return;
				}

				ret.then((execute) => {
					execute !== false && executeRoute();
				});
			};

			if (routeInterceptors.size) {
				// See note on `routeInterceptors`
				routeInterceptors.get(
					Array.from(routeInterceptors.keys())[routeInterceptors.size - 1],
				)(_route, fragment, name, args);
				return;
			}

			_route();
		});
		return this;
	}

	/*
	 * reroute() is useful in a route handler that wants to trigger a route;
	 * it uses replaceState() to delete the intermediate route from the history.
	 * @param {string} frag The url component
	 * @param {object} opts Options for .navigate();
	 */
	reroute(frag, opts) {
		// Replace the #upload in the browser history so that the back button won't re-forward:
		// Note the blank 'state object' because I haven't found the doc on what that should contain;
		// I think it's up to us, but i hope Backbone isn't using it ...
		history.replaceState({}, '', `#${frag}`);

		// Tidy up our local .history
		this.history[0] = frag;

		// This will trigger the route, even though our href is here already:
		this.navigate(frag, opts);
	}

	/**
	 * Add a set of new routes to the routes we already have.
	 * @param {object} routes Routes
	 */
	addRoutes(routes) {
		// apparently, routes must be added in reverse order to their position in the object.
		_.keys(routes)
			.reverse()
			.forEach((routePattern) =>
				this.route(routePattern, routes[routePattern]),
			);
	}

	/**
	 * inCategory() parses the current route to see if it's a category route
	 * @return {number} a route ID, or undefined if not a category route.
	 */
	inCategory() {
		if (Backbone.history.fragment?.match(/category\/(\d+)/)) {
			return parseInt(
				Backbone.history.fragment.match(/category\/(\d+)/)[1],
				10,
			);
		} else {
			return undefined;
		}
	}

	/**
	 * push() and pop() have had problems.
	 * New strategy: find the most recent route that does *not* match a pattern, and go to that.
	 * @param {string} pattern Pattern
	 * @param {object} opts Options
	 */
	exitRoute(pattern, opts = {}) {
		const targetRoute = _.find(this.history, (r) => {
			// Only use match if RegExp was passed
			if (pattern instanceof RegExp) {
				// Find the first route that doesn't match this pattern
				return !r.match(pattern);
			}
			// Otheriwse we do an exact comparison
			return r !== pattern;
		});

		const {force, ...sendOpts} = opts;
		// If there is no available history, or it happens to be the last route
		// use this.back(), it will fall back on home()
		if (!targetRoute || (!force && targetRoute === this.history[1])) {
			if (force && !targetRoute && this.history.length) return this.home();
			return this.back({trigger: true});
		}

		this.navigate(targetRoute || '', {trigger: true, ...sendOpts});
	}

	/**
	 * Route to the HOME_ROUTE, or the null route if HOME_ROUTE is not defined.
	 * If no options provided, we set {trigger: true} to actually fire the route handler -- which is what we usually want.
	 * @param {object} opts Options
	 */
	home(opts = {}) {
		opts.trigger = opts.trigger !== false;
		this.navigate(coreConfig.HOME_ROUTE || '#', opts);
	}
}

export default AppRouter;
